import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import AppShell from '../components/AppShell';
import CategoryManager from '../components/catalog/CategoryManager';
import ContainerManager from '../components/catalog/ContainerManager';

const UNITS = { piece:'шт', liter:'л', gram:'кг' };
const UNIT_OPTIONS = [
  { value:'piece', label:'Штука (шт)' },
  { value:'liter', label:'Літри (л)' },
  { value:'gram', label:'Грами (г)' },
];

const emptyForm = {
  name:'', price:'', price_override:'', cost_price:'', unit:'piece',
  is_draft:false, is_weight:false, is_excise:false,
  uktzed:'', tax_group:'',
  stock_quantity:'0', low_stock_threshold:'', barcode:'',
  category_id: '',
};

export default function CatalogPage() {
  const { id: storeId } = useParams();
  const navigate = useNavigate();
  const { isStoreAdmin, hasAccessToStore } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showCatManager, setShowCatManager] = useState(false);
  const [showContainerManager, setShowContainerManager] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [existingProductId, setExistingProductId] = useState(null); // обраний з мережевого каталогу (щоб не дублювати)
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [filterUnit, setFilterUnit] = useState('all');
  const [error, setError] = useState('');
  const suggestRef = useRef(null);
  const canEdit = isStoreAdmin(storeId);

  useEffect(() => {
    if (!hasAccessToStore(storeId)) { navigate('/dashboard'); return; }
    loadData();
  }, [storeId]);

  useEffect(() => {
    function handleClick(e) {
      if (suggestRef.current && !suggestRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function loadData() {
    setLoading(true);
    const [p, c] = await Promise.all([
      api.get(`/catalog/stores/${storeId}/products`).then(r => r.data).catch(() => []),
      api.get(`/catalog/stores/${storeId}/categories`).then(r => r.data).catch(() => []),
    ]);
    setProducts(p);
    setCategories(c);
    setLoading(false);
  }

  async function searchSuggestions(q) {
    setForm(f => ({...f, name: q}));
    setExistingProductId(null); // ручне редагування назви → це новий товар
    if (q.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    try {
      const res = await api.get(`/catalog/suggestions?q=${encodeURIComponent(q)}&store_id=${storeId}`);
      setSuggestions(res.data);
      setShowSuggestions(res.data.length > 0);
    } catch { setSuggestions([]); }
  }

  // Вибір товару з мережевого каталогу → не створюємо дубль, лише заведемо наявність у цьому магазині.
  function applySuggestion(s) {
    setForm(f => ({...f, name:s.name, price:s.price, unit:s.unit, is_draft:s.is_draft, is_excise:s.is_excise}));
    setExistingProductId(s.product_id || null);
    setShowSuggestions(false);
  }

  async function submitForm(e) {
    e.preventDefault(); setError('');
    // Поля магазину (наявність/собівартість/ціна-перевизначення) — спільні для всіх випадків.
    const storeFields = {
      cost_price: form.cost_price || '0',
      stock_quantity: form.stock_quantity || '0',
      low_stock_threshold: form.low_stock_threshold || null,
      is_active: true,
    };
    if (form.price_override !== '' && form.price_override != null) {
      storeFields.price_override = form.price_override;
    } else {
      storeFields.reset_price_override = true; // прибрати перевизначення → діє мережева ціна
    }
    try {
      if (editingId) {
        // Правка: мережеві поля (назва/ціна/категорія) + поля магазину.
        await api.patch(`/catalog/stores/${storeId}/products/${editingId}`, {
          name: form.name, price: form.price, category_id: form.category_id || null,
          barcode: form.barcode || null,
          is_excise: form.is_excise, uktzed: form.uktzed, tax_group: form.tax_group,
          ...storeFields,
        });
      } else if (existingProductId) {
        // Товар вже є в мережевому каталозі → лише заводимо наявність у цьому магазині (без дубля).
        await api.patch(`/catalog/stores/${storeId}/products/${existingProductId}`, storeFields);
      } else {
        // Новий товар у мережевому каталозі + наявність у цьому магазині.
        await api.post(`/catalog/stores/${storeId}/products`, {
          name: form.name, price: form.price, unit: form.unit,
          is_excise: form.is_excise, barcode: form.barcode || null,
          uktzed: form.uktzed || null, tax_group: form.tax_group || null,
          category_id: form.category_id || null,
          cost_price: form.cost_price || '0',
          stock_quantity: form.stock_quantity || '0',
          low_stock_threshold: form.low_stock_threshold || null,
        });
      }
      await loadData();
      setShowForm(false);
      setForm(emptyForm);
      setEditingId(null);
      setExistingProductId(null);
    } catch(err) {
      const detail = err.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : (detail || 'Помилка'));
    }
  }

  // Швидке перемикання «продається в цьому магазині».
  async function toggleActive(p) {
    try {
      await api.patch(`/catalog/stores/${storeId}/products/${p.id}`, { is_active: !p.is_active });
      await loadData();
    } catch(err) { /* no-op */ }
  }

  async function deleteProduct(productId) {
    if (!window.confirm('Прибрати товар з продажу в цьому магазині? (у мережевому каталозі він залишиться)')) return;
    await api.delete(`/catalog/stores/${storeId}/products/${productId}`);
    await loadData();
  }

  function startEdit(product) {
    setForm({
      name: product.name,
      price: product.base_price ?? product.price,           // мережева ціна
      price_override: product.price_override || '',          // ціна саме в цьому магазині
      cost_price: product.cost_price,
      unit: product.unit, is_draft: product.is_draft, is_weight: product.is_weight,
      is_excise: product.is_excise, stock_quantity: product.stock_quantity,
      uktzed: product.uktzed || '', tax_group: product.tax_group || '',
      low_stock_threshold: product.low_stock_threshold || '',
      barcode: product.barcode || '',
      category_id: product.category_id || '',
    });
    setEditingId(product.id);
    setExistingProductId(null);
    setShowForm(true);
    setError('');
  }

  const filtered = products.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === 'all' || p.category_id === filterCat ||
      (filterCat === 'none' && !p.category_id);
    const matchUnit = filterUnit === 'all' || p.unit === filterUnit;
    return matchSearch && matchCat && matchUnit;
  });

  const lowStock = products.filter(p => p.low_stock_threshold && parseFloat(p.stock_quantity) <= parseFloat(p.low_stock_threshold));

  function renderCatIcon(photo_url) {
    if (!photo_url) return null;
    if (photo_url.startsWith('http')) return <img src={photo_url} alt="" style={{width:'20px',height:'20px',objectFit:'cover',borderRadius:'4px'}} />;
    return <span>{photo_url}</span>;
  }

  return (
    <AppShell
      title="📦 Асортимент"
      onBack={() => navigate(`/store/${storeId}`)}
    >
      {canEdit && (
        <div className="row" style={{ justifyContent:'flex-end', marginBottom:18 }}>
          <button className="btn btn-soft btn-sm" onClick={() => setShowCatManager(!showCatManager)}>
            📂 Категорії
          </button>
          <button className="btn btn-soft btn-sm" onClick={() => setShowContainerManager(!showContainerManager)}>
            🍶 Тара
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => {
            setShowForm(true); setForm(emptyForm); setEditingId(null); setError('');
          }}>
            + Товар
          </button>
        </div>
      )}

      <div>
        {lowStock.length > 0 && (
          <div style={styles.alertBox}>
            ⚠️ Низький залишок: {lowStock.map(p => p.name).join(', ')}
          </div>
        )}

        {/* Менеджер категорій */}
        {showCatManager && canEdit && (
          <CategoryManager
            storeId={storeId}
            categories={categories}
            onUpdate={loadData}
          />
        )}

        {showContainerManager && canEdit && (
          <ContainerManager storeId={storeId} products={products} categories={categories} />
        )}

        {/* Форма товару */}
        {showForm && canEdit && (
          <div style={styles.formCard}>
            <h3 style={styles.formTitle}>{editingId ? 'Редагувати товар' : 'Новий товар'}</h3>
            <form onSubmit={submitForm}
              onKeyDown={e => { if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault(); }}>
              <div style={styles.fieldGroup} ref={suggestRef}>
                <label style={styles.label}>Назва товару</label>
                <input style={styles.input} placeholder="Почніть вводити назву..."
                  value={form.name} onChange={e => searchSuggestions(e.target.value)}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)} required />
                {showSuggestions && (
                  <div style={styles.suggestions}>
                    <div style={styles.suggestHeader}>💡 З мережевого каталогу (без дублювання):</div>
                    {suggestions.map((s, i) => (
                      <div key={i} style={styles.suggestItem} onClick={() => applySuggestion(s)}>
                        <span>{s.name}</span>
                        <span style={styles.suggestMeta}>{s.price} ₴/{UNITS[s.unit]}{s.is_draft&&' 🍺'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={styles.row}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Категорія</label>
                  <select style={styles.input} value={form.category_id}
                    onChange={e => setForm(f => ({...f, category_id: e.target.value}))}>
                    <option value="">Без категорії</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.photo_url && !c.photo_url.startsWith('http') ? c.photo_url + ' ' : ''}{c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Одиниця виміру</label>
                  <select style={styles.input} value={form.unit}
                    onChange={e => setForm(f => ({...f, unit: e.target.value}))} disabled={!!editingId}>
                    {UNIT_OPTIONS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                </div>
              </div>

              <div style={styles.row}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Мережева ціна (₴) {form.unit === 'gram' ? '— за кг' : form.unit === 'liter' ? '— за літр' : ''}</label>
                  <input style={styles.input} type="number" step="0.01" placeholder="0.00"
                    value={form.price} onChange={e => setForm(f => ({...f, price:e.target.value}))}
                    disabled={!!existingProductId} required />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Ціна в цьому магазині (₴)</label>
                  <input style={styles.input} type="number" step="0.01" placeholder="як мережева"
                    value={form.price_override} onChange={e => setForm(f => ({...f, price_override:e.target.value}))} />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Собівартість (₴)</label>
                  <input style={styles.input} type="number" step="0.01" placeholder="0.00"
                    value={form.cost_price} onChange={e => setForm(f => ({...f, cost_price:e.target.value}))} />
                </div>
              </div>

              <div style={styles.row}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Залишок ({UNITS[form.unit]})</label>
                  <input style={styles.input} type="number" step="0.001"
                    value={form.stock_quantity} onChange={e => setForm(f => ({...f, stock_quantity:e.target.value}))} />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Поріг залишку ({UNITS[form.unit]})</label>
                  <input style={styles.input} type="number" step="0.001" placeholder="Не встановлено"
                    value={form.low_stock_threshold} onChange={e => setForm(f => ({...f, low_stock_threshold:e.target.value}))} />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Штрихкод</label>
                  <input style={styles.input} placeholder="Необов'язково"
                    value={form.barcode} onChange={e => setForm(f => ({...f, barcode:e.target.value}))} />
                </div>
              </div>

              <div style={styles.checkboxRow}>
                <label style={styles.checkbox}>
                  <input type="checkbox" checked={form.is_excise}
                    onChange={e => setForm(f => ({...f, is_excise:e.target.checked}))} />
                  🏷 Акцизне
                </label>
                <div style={styles.autoFlags}>
                  {form.unit === 'liter' && <span style={styles.autoFlag}>🍺 Розливне (авто)</span>}
                  {form.unit === 'gram' && <span style={styles.autoFlag}>⚖️ Вагове (авто)</span>}
                </div>
              </div>

              <div style={styles.row}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Код УКТ ЗЕД (для чека ПРРО)</label>
                  <input style={styles.input} placeholder="10 цифр, напр. 2203001000"
                    value={form.uktzed} onChange={e => setForm(f => ({...f, uktzed:e.target.value}))} />
                  <span style={styles.hint}>Беріть з видаткової накладної постачальника. Пиво розлив 2203001000, сидр 2206008100, вино 2204298100. Обов'язковий для підакцизних.</span>
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Податкова група</label>
                  <input style={styles.input} placeholder={form.is_excise ? 'Д (акциз 5%)' : 'порожньо'}
                    value={form.tax_group} onChange={e => setForm(f => ({...f, tax_group:e.target.value}))} />
                  <span style={styles.hint}>Літера групи ПРРО. Для алкоголю на єдиному податку — «Д» (акциз 5%). Для неакцизних — порожньо.</span>
                </div>
              </div>

              {error && <p style={styles.error}>{error}</p>}
              <div style={styles.formButtons}>
                <button type="submit" style={styles.saveBtn}>{editingId ? 'Зберегти' : 'Додати товар'}</button>
                <button type="button" style={styles.cancelBtn}
                  onClick={() => { setShowForm(false); setForm(emptyForm); setEditingId(null); }}>
                  Скасувати
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Фільтри */}
        <div style={styles.filters}>
          <input style={styles.searchInput} placeholder="🔍 Пошук товару..."
            value={search} onChange={e => setSearch(e.target.value)} />

          {/* Фільтр по категоріях */}
          <div style={styles.catFilters}>
            <button style={filterCat==='all' ? styles.catFilterActive : styles.catFilter}
              onClick={() => setFilterCat('all')}>
              Всі
            </button>
            <button style={filterCat==='none' ? styles.catFilterActive : styles.catFilter}
              onClick={() => setFilterCat('none')}>
              Без категорії
            </button>
            {categories.map(c => (
              <button key={c.id}
                style={filterCat===c.id ? styles.catFilterActive : styles.catFilter}
                onClick={() => setFilterCat(c.id)}>
                {c.photo_url && !c.photo_url.startsWith('http') ? c.photo_url + ' ' : ''}
                {c.name}
              </button>
            ))}
          </div>

          <select style={styles.filterSelect} value={filterUnit} onChange={e => setFilterUnit(e.target.value)}>
            <option value="all">Всі типи</option>
            {UNIT_OPTIONS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
          </select>
          <span style={styles.count}>{filtered.length} товарів</span>
        </div>

        {/* Таблиця */}
        {loading ? <p className="hint">Завантаження...</p> : filtered.length === 0 ? (
          <p className="hint">{products.length === 0 ? 'Додайте перший товар!' : 'Нічого не знайдено'}</p>
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Назва</th>
                  <th>Категорія</th>
                  <th style={{textAlign:'center'}}>Тип</th>
                  <th style={{textAlign:'right'}}>Ціна</th>
                  <th style={{textAlign:'right'}}>Залишок</th>
                  {canEdit && <th style={{textAlign:'center'}}>Дії</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => {
                  const cat = categories.find(c => c.id === p.category_id);
                  const isLow = p.low_stock_threshold && parseFloat(p.stock_quantity) <= parseFloat(p.low_stock_threshold);
                  const soldHere = p.is_active;
                  return (
                    <tr key={p.id} style={soldHere ? undefined : {opacity:0.5}}>
                      <td>
                        {p.name}
                        {p.is_draft && <span style={styles.tag}>🍺</span>}
                        {p.is_excise && <span style={styles.tag}>🏷</span>}
                        {p.is_weight && <span style={styles.tag}>⚖️</span>}
                        {p.price_override != null && <span style={styles.tag} title="Ціна перевизначена для цього магазину">💲</span>}
                        {!soldHere && <span className="muted" style={{marginLeft:6, fontSize:11}}>не продається тут</span>}
                      </td>
                      <td>
                        {cat ? (
                          <span className="badge">
                            {cat.photo_url && !cat.photo_url.startsWith('http') ? cat.photo_url + ' ' : ''}
                            {cat.name}
                          </span>
                        ) : <span className="muted">—</span>}
                      </td>
                      <td style={{textAlign:'center'}} className="muted">{UNITS[p.unit]}</td>
                      <td style={{textAlign:'right', color:'var(--amber)', fontWeight:700, whiteSpace:'nowrap'}}>
                        {parseFloat(p.price).toFixed(2)} ₴
                      </td>
                      <td style={{textAlign:'right', whiteSpace:'nowrap', color: isLow ? 'var(--red)' : 'var(--green)'}}>
                        {parseFloat(p.stock_quantity).toFixed(p.unit==='piece'?0:p.unit==='gram'?3:2)} {UNITS[p.unit]}
                        {isLow && ' ⚠️'}
                      </td>
                      {canEdit && (
                        <td>
                          <div className="row" style={{gap:6, justifyContent:'center', flexWrap:'nowrap'}}>
                            {soldHere ? (
                              <>
                                <button className="btn btn-soft btn-sm" onClick={() => startEdit(p)}>✏️</button>
                                <button className="btn btn-danger btn-sm" title="Прибрати з продажу тут" onClick={() => deleteProduct(p.id)}>🗑</button>
                              </>
                            ) : (
                              <button className="btn btn-primary btn-sm" title="Додати в продаж у цьому магазині" onClick={() => toggleActive(p)}>+ У магазин</button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}

const styles = {
  alertBox: { background:'#4a3000', border:'1px solid #f5a623', color:'#f5a623', borderRadius:'8px', padding:'12px 16px', marginBottom:'24px', fontSize:'14px' },
  formCard: { background:'#211c14', border:'1px solid #38301f', borderRadius:'12px', padding:'24px', marginBottom:'24px' },
  formTitle: { color:'#f5a623', marginTop:0, marginBottom:'20px' },
  row: { display:'flex', gap:'16px', flexWrap:'wrap' },
  fieldGroup: { flex:1, minWidth:'160px', position:'relative', marginBottom:'14px' },
  label: { display:'block', color:'#9a8a6e', fontSize:'11px', marginBottom:'5px', textTransform:'uppercase', letterSpacing:'0.5px' },
  input: { width:'100%', padding:'10px 14px', borderRadius:'8px', border:'1px solid #38301f', background:'#1c1812', color:'#fff', fontSize:'14px', boxSizing:'border-box' },
  suggestions: { position:'absolute', top:'100%', left:0, right:0, background:'#1c1812', border:'1px solid #f5a623', borderRadius:'8px', zIndex:100, marginTop:'4px' },
  suggestHeader: { padding:'8px 12px', color:'#f5a623', fontSize:'12px', borderBottom:'1px solid #38301f' },
  suggestItem: { padding:'9px 12px', cursor:'pointer', display:'flex', justifyContent:'space-between', borderBottom:'1px solid #14110c' },
  suggestMeta: { color:'#9a8a6e', fontSize:'12px' },
  checkboxRow: { display:'flex', gap:'20px', marginBottom:'16px' },
  checkbox: { display:'flex', alignItems:'center', gap:'8px', cursor:'pointer', fontSize:'14px', color:'#9a8a6e' },
  error: { color:'#ff6b6b', fontSize:'13px', marginBottom:'10px' },
  hint: { display:'block', color:'#6b5d44', fontSize:'11px', marginTop:'4px', lineHeight:1.4 },
  formButtons: { display:'flex', gap:'10px' },
  saveBtn: { padding:'10px 24px', background:'#f5a623', color:'#14110c', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer' },
  cancelBtn: { padding:'10px 16px', background:'transparent', color:'#9a8a6e', border:'1px solid #38301f', borderRadius:'8px', cursor:'pointer' },
  filters: { marginBottom:'16px' },
  searchInput: { width:'100%', padding:'10px 14px', borderRadius:'8px', border:'1px solid #38301f', background:'#211c14', color:'#fff', fontSize:'14px', boxSizing:'border-box', marginBottom:'10px' },
  catFilters: { display:'flex', flexWrap:'wrap', gap:'6px', marginBottom:'10px' },
  catFilter: { padding:'6px 14px', background:'#38301f', color:'#9a8a6e', border:'none', borderRadius:'20px', cursor:'pointer', fontSize:'13px' },
  catFilterActive: { padding:'6px 14px', background:'#f5a623', color:'#14110c', border:'none', borderRadius:'20px', cursor:'pointer', fontSize:'13px', fontWeight:'bold' },
  filterSelect: { padding:'8px 12px', borderRadius:'8px', border:'1px solid #38301f', background:'#211c14', color:'#fff', fontSize:'13px', marginRight:'12px' },
  count: { color:'#9a8a6e', fontSize:'13px' },
  tag: { marginLeft:'5px', fontSize:'12px' },
  autoFlags: { display:'flex', gap:'8px' },
  autoFlag: { color:'#9a8a6e', fontSize:'13px', background:'#38301f', padding:'4px 10px', borderRadius:'12px' },
};
